// PRISAI React Context for Auth
// SECURE VERSION - No auto-login

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { User } from '@/types';
import api from '@/lib/api';
import { clearAuthState, setAuthState } from '@/lib/auth';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setApiKey: (key: string) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const { user: loggedInUser } = await api.login(email, password);
      setUser(loggedInUser);
      setAuthState({
        isAuthenticated: true,
        user: loggedInUser,
        apiKey: null,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
      clearAuthState();
    }
  }, []);

  const setApiKey = useCallback((key: string) => {
    api.setApiKey(key);
    setUser({ id: 'api', email: 'api-user', password_hash: '', created_at: '', role: 'user' });
    setAuthState({
      isAuthenticated: true,
      user: { id: 'api', email: 'api-user', password_hash: '', created_at: '', role: 'user' },
      apiKey: key,
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        setApiKey,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
